import React from 'react';
import { TwitterIcon } from 'icons/twitter';
import { FacebookIcon } from 'icons/facebook';
import { TelegramIcon } from 'icons/telegram';
import { RedditIcon } from 'icons/reddit';

/**
 * A message to be shared on social media.
 *
 * Not using the native {@link ShareData} type since this is
 * more typesafe.
 */
export type SocialMediaShareData = {
  title?: string;
  text: string;
  /**
   * Share targets need a link to be able to share something
   */
  link: URL;
};

export const toNativeShareData = (data: SocialMediaShareData): ShareData => ({
  url: data.link.toString(),
  text: data.text,
  title: data.title,
});

export function canShareNatively(payload: SocialMediaShareData): boolean {
  if (!('canShare' in window.navigator) || !('share' in window.navigator)) {
    return false;
  }
  return window.navigator.canShare(toNativeShareData(payload));
}

export type SocialMedia = {
  displayName: string;
  icon: React.ReactElement;
  sharingLink(data: SocialMediaShareData): URL;
};

export const socials = {
  twitter: {
    displayName: 'Twitter',
    icon: <TwitterIcon />,
    sharingLink(data: SocialMediaShareData): URL {
      const params = new URLSearchParams({ text: data.text });

      return new URL(`https://twitter.com/intent/tweet?${params}`);
    },
  } as SocialMedia,
  facebook: {
    displayName: 'Facebook',
    icon: <FacebookIcon />,
    sharingLink(data: SocialMediaShareData): URL {
      const params = new URLSearchParams({
        u: data.link.toString(),
        href: data.link.toString(),
      });

      return new URL(`https://www.facebook.com/sharer/sharer.php?${params}`);
    },
  } as SocialMedia,
  telegram: {
    displayName: 'Telegram',
    icon: <TelegramIcon />,
    /**
     * Telegram prepends the url to the message when sharing so
     * we usually don't want to include the url in the message.
     * @param data
     */
    sharingLink(data: SocialMediaShareData): URL {
      const params = new URLSearchParams({
        url: data.link.toString(),
        text: data.text,
      });

      return new URL(`https://t.me/share/url?${params}`);
    },
  } as SocialMedia,
  reddit: {
    displayName: 'Reddit',
    icon: <RedditIcon />,
    sharingLink(data: SocialMediaShareData): URL {
      const rawParams: Record<string, string> = {
        url: data.link.toString(),
        title: data.text,
      };

      if (data.title) {
        rawParams.title = data.title;
      }

      const params = new URLSearchParams(rawParams);
      return new URL(`https://www.reddit.com/submit?${params}`);
    },
  } as SocialMedia,
};

export type SocialMediaType = keyof typeof socials;

export type OpenSeaItem = {
  contract: string;
  nftId: number;
};

export const nftMarketplaceUrl = {
  openSea(item: OpenSeaItem): URL {
    return new URL(`https://opensea.io/assets/ethereum/${item.contract}/${item.nftId}`);
  },
  poap(tokenId: string): URL {
    return new URL(`https://app.poap.xyz/token/${tokenId}`);
  },
};
